import { Typography, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { Fragment, ReactElement, useCallback, useEffect } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { PracticeAssignOperatorCrumbs } from 'practice/constants/practice-crubs';
import { validationSchemaAssignOperator } from 'practice/constants/practice-research-validation';
import AssignAccordion from 'practice/components/assign-practice-operator/assign-form';
import OldAssignedOperators from 'practice/components/assign-practice-operator/old-assign';
import { FormAssignOperatorVM } from 'practice/view-models/practice-assign';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useNavigate, useParams } from 'react-router-dom';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { DateTime } from 'luxon';
import { HistoryPracticeOperator } from 'shell/models/history-practice-operator';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { PracticeSearchType, PracticeFilterSearchType } from '../storage-practice';
import { useFilterTable } from 'shell/hooks/use-filter-table';

const PracticeAssignOperatorPage = (): ReactElement => {
  const filterTable = useFilterTable<PracticeSearchType>();
  const navigate = useNavigate();
  const params = useParams();

  const redirectUri = window.location.search.replace('?redirectUri=', '') || '';

  const [practices] = useHttpFetch<HttpResponseList<HistoryPracticeOperator>, FormAssignOperatorVM>(
    `${__PRACTICE_API_URL__}/storico-operatori-pratica/${params.codiceEnte}-${params.idAnnoPratica}-${params.tipoRegistro}-${params.progressivoPratica}`,
    {
      onBootstrap: true,
      transformer: (response) => {
        const responseData = response.data.payload?.list || [];
        const [actualElement, ...otherElements] = responseData;

        return {
          oldService: actualElement?.descrizioneServizio || actualElement?.codiceServizio,
          oldOperator: actualElement?.nomeOperatore || actualElement?.codiceOperatorePratica,
          oldOperatorsAssigned: otherElements.map((otherElement) => ({
            date: otherElement.timestampCambioOperatore
              ? DateTime.fromISO(otherElement.timestampCambioOperatore)
              : null,
            operator: otherElement.nomeOperatore || otherElement?.codiceOperatorePratica || '',
            reason: otherElement.motivoCambioOperatore || '',
            service: otherElement.descrizioneServizio || otherElement?.codiceServizio || '',
          })),
        };
      },
    }
  );

  useEffect(() => {
    if (
      filterTable?.values?.searchType === PracticeFilterSearchType.SEARCH_BY_PRATICE ||
      filterTable?.values?.searchType === PracticeFilterSearchType.SEARCH_BY_PERSON
    ) {
      filterTable?.setValues({ ...filterTable?.values, isChildComponentVisited: true });
    }
  }, []);

  const assignOperator = useHttpClient(
    `${__PRACTICE_API_URL__}/storico-operatori-pratica/nuova-assegnazione`,
    {
      options: {
        method: 'POST',
        data: {},
      },
    }
  );

  const onSubmit = useCallback(
    (values: FormAssignOperatorVM) => {
      (
        assignOperator({
          data: {
            listStoricoOperatorePratica: [
              {
                codiceEnte: params.codiceEnte,
                tipoRegistro: params.tipoRegistro,
                idAnnoPratica: params.idAnnoPratica,
                progressivoPratica: params.progressivoPratica,
              },
            ],
            codiceServizio: values.newService,
            codiceOperatore: values.newOperator,
            motivoCambioOperatore: values.reason,
          },
        }) as ResponsePromise<void>
      ).then(() => {
        toast.success('Operazione eseguita con successo');
        navigate('/pratiche/ricerca');
      });
    },
    [assignOperator, params, navigate]
  );

  const hasEndedLoading = !practices.isLoading && !practices.pristine && !practices.hasErrors;

  const handleRedirect = useEventCallback(() => {
    navigate(redirectUri);
  });

  return (
    <Fragment>
      <div className="pmi-d-block pmi-d-md-flex pmi-justify-content-between">
        <Breadcrumbs items={PracticeAssignOperatorCrumbs} />
        {redirectUri ? (
          <div className="pmi-my-2 pmi-w-30 pmi-w-lg-20">
            <PmiButton onClick={handleRedirect} fullWidth variant="outlined" color="cdmBrand">
              Torna indietro
            </PmiButton>
          </div>
        ) : null}
      </div>
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Assegnazione pratica altro operatore
        </Typography>
      </div>
      {hasEndedLoading ? (
        <Formik
          validationSchema={validationSchemaAssignOperator}
          initialValues={
            {
              ...((practices.data as FormAssignOperatorVM) || {}),
              newService: '',
              newOperator: '',
              reason: '',
            } as FormAssignOperatorVM
          }
          onSubmit={onSubmit}
        >
          <Form>
            <AssignAccordion />
            <OldAssignedOperators />
          </Form>
        </Formik>
      ) : null}
    </Fragment>
  );
};

PracticeAssignOperatorPage.displayName = 'Practice Assign Operator Page component';

export default PracticeAssignOperatorPage;
